import { TasksList } from '@dispatch/web/features/tasks';
import {
  MapProvider,
  useDeleteTaskFilter,
  useGetResourcesPaginated,
  useGetTaskFilters,
  useGetTaskInfos,
  useGetUnassignedTasks,
  useUpsertTaskListFilter,
  useGetCompanyAttributeOptions,
  useGetMultiResources,
  useCurrentUserResource,
  useCurrentUserAsResourceId,
  useGetCountyOptionsWithState,
  usePermissions,
  useGetUnassignedTaskCount,
  useShowLocationBasedEvents,
  useCompany,
  useGetCompanyRoleOptions,
  useGetTaskFilterGroups,
  useDeleteTaskFilterGroup,
  useUpsertTaskListFilterGroup,
  useFiltersOrderIndexBulkUpdate,
  useGroupsOrderIndexBulkUpdate,
  useFilter,
} from '@dispatch/shared/hooks';
import { useCurrentUser } from '@dispatch/web/auth';
import { ErrorBoundary } from '@dispatch/web/ui';
import { BladeErrorFallback } from '@dispatch/shared/errors';
import { trackError } from '@dispatch/web/telemetry';
import {
  defaultView,
  MultiSelectOption,
  ResourceAttribute,
  TaskInfo,
  TasksListFilter,
  TaskPeril,
  TaskFilterGroup,
} from '@dispatch/shared/types';
import React, { useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  useNotification,
  useNotificationMutationFactory,
} from '@dispatch/web/ui';
import { format } from 'date-fns';
import { useQueryClient } from 'react-query';
import { GridSortItem, GridSortModel } from '@mui/x-data-grid';
import { useDebounce, useLocalStorage } from 'react-use';
import { useValidateTaskErrors } from './utils/ErrorHandlers';
import _, { isEmpty } from 'lodash';
import { useGeneralDataContext } from '@dispatch/shared/hooks';

const defaultTimeZone = 'America/New_York';
const PERIL = 'peril';
const ASSIGNMENT_TYPE = 'assignment type';

export const TasksListPage = () => {
  // scheduler state (this needs to be here, because the api calls happen in this component)
  // when date is invalidated this componen rerenders which triggers a rerender of the schdeuler
  // without this here the scheduler start/end resets when it should not.
  const { permissions } = usePermissions();
  const {
    company,
    offices,
    teams,
    groups,
    perils,
    rescheduleReasons,
    perilOptions,
    regionOptions,
  } = useCompany();
  const officeOptions = React.useMemo(() => {
    return offices?.map((office) => ({
      title: office.name,
      value: office.integrationGUID,
    })) as MultiSelectOption[];
  }, [company]);

  const { companyRoles } = useGetCompanyRoleOptions(company.role);

  const teamOptions = React.useMemo(() => {
    return teams?.map((team) => ({
      title: team.name,
      value: team.integrationGUID,
    })) as MultiSelectOption[];
  }, [company]);

  const [multiSelected, setMultiSelected] = React.useState<
    { id: string; name: string }[]
  >([]);
  const [viewState, setViewState] = React.useState(defaultView);
  const [resourceFilterInput, setResourceFilterInput] = useState<string>('');
  const [resourceFilterDebouncedValue, setResourceFilterDebouncedValue] =
    React.useState('');
  const [lossCountyFilterInput, setLossCountyFilterInput] =
    useState<string>('');
  const [lossStateFilterInput, setLossStateFilterInput] = useState<string[]>(
    []
  );
  const [lossCountyFilterDebouncedValue, setLossCountyFilterDebouncedValue] =
    React.useState('');
  const [rsrcCountyFilterInput, setRsrcCountyFilterInput] =
    useState<string>('');
  const [rsrcStateFilterInput, setRsrcStateFilterInput] = useState<string[]>(
    []
  );
  const [rsrcCountyFilterDebouncedValue, setRsrcCountyFilterDebouncedValue] =
    React.useState('');
  const [cachedTimeZone, setTimeZoneCachedValue] = useLocalStorage(
    'timeZone',
    defaultTimeZone
  );
  const [isNonWorkingTimeFiltered, setIsNonWorkingTimeFiltered] =
    React.useState(true);

  const cachedTimeZoneValue = React.useMemo(() => {
    return cachedTimeZone || defaultTimeZone;
  }, [cachedTimeZone]);

  useDebounce(
    () => {
      setResourceFilterDebouncedValue(resourceFilterInput);
    },
    1000,
    [resourceFilterInput]
  );

  useDebounce(
    () => {
      setLossCountyFilterDebouncedValue(lossCountyFilterInput);
    },
    1000,
    [lossCountyFilterInput]
  );

  useDebounce(
    () => {
      setRsrcCountyFilterDebouncedValue(rsrcCountyFilterInput);
    },
    1000,
    [rsrcCountyFilterInput]
  );

  const selectedResources = useGetMultiResources(
    multiSelected,
    multiSelected?.length > 0
  );

  const filteredResources = useGetResourcesPaginated({
    filters: {
      resourceName: resourceFilterDebouncedValue,
      isActive: true,
      pageSize: 25, //search only first 25
    },
    enabled: !!resourceFilterDebouncedValue,
  });

  const filteredLossCounties = useGetCountyOptionsWithState(
    lossCountyFilterDebouncedValue,
    !!lossCountyFilterDebouncedValue,
    lossStateFilterInput
  );

  const filteredRsrcCounties = useGetCountyOptionsWithState(
    rsrcCountyFilterDebouncedValue,
    !!rsrcCountyFilterDebouncedValue,
    rsrcStateFilterInput
  );

  const companyAttributes = useGetCompanyAttributeOptions(
    company?.attributeDefinition || []
  );
  const mappedFilteredResources = useMemo(() => {
    if (filteredResources.status === 'success' && filteredResources.data) {
      return filteredResources.data?.map((resource): MultiSelectOption => {
        return {
          title: resource.name,
          value: resource.integrationGUID,
        };
      });
    }
  }, [filteredResources.data, filteredResources.status]);

  const attributeOptions = React.useMemo(() => {
    return company?.attributeDefinition?.flatMap((attribute) => {
      if (attribute.displayName.toLowerCase().includes(PERIL)) return [];
      if (attribute.displayName.toLowerCase().includes(ASSIGNMENT_TYPE))
        return [];

      return attribute?.attributeDefinitionValues?.map((value) => {
        //combine title if its just active, because there can be a lot like this
        const combinedTitle = `${attribute.displayName} = ${value.displayName}`;

        return {
          title: value.displayName.toLowerCase().includes('active')
            ? combinedTitle
            : value.displayName,
          value: value.integrationGUID,
        };
      });
    });
  }, [company.attributeDefinition]);

  const { userId, tenantId } = useCurrentUser();
  const { showLocationBasedEvents } = useShowLocationBasedEvents(
    tenantId || ''
  );

  const { isAdjuster } = useCurrentUserResource();
  const currentUser = useCurrentUserAsResourceId();

  React.useEffect(() => {
    if (isAdjuster) {
      setFilters({
        ...filters,
        assignedResourceIds: [String(currentUser.value)],
      });
    }
  }, [currentUser, isAdjuster]);

  const defaultFilters: TasksListFilter = useMemo(() => {
    return {
      pageSize: 10,
      status: 'all',
      assignedResourceIds: isAdjuster ? [String(currentUser.value)] : [],
    };
  }, [currentUser, isAdjuster]);

  const { notifyError, notifySuccess } = useNotification();
  const { storedFilters, fetchStoredFilters } = useGetTaskFilters(userId || '');
  const { storedFilterGroups, fetchStoredFilterGroups } =
    useGetTaskFilterGroups(userId || '');

  const storedFiltersWithGroups = React.useMemo(() => {
    if (storedFilters && storedFilterGroups) {
      return [...storedFilterGroups, ...storedFilters];
    }
    return [];
  }, [storedFilters, storedFilterGroups]);

  const { statesOptions, methodOfInspectionOptions } = useGeneralDataContext();

  // Read filter context — task filters persist under contextFilters.taskFilters
  const { filters: contextFilters } = useFilter();

  // Initialize filters from context if available, otherwise use defaults.
  // Task filters are stored under contextFilters.taskFilters when using grouped context.
  const getInitialFilters = (): TasksListFilter => {
    const taskFilters = contextFilters?.taskFilters ?? contextFilters;
    if (
      taskFilters &&
      typeof taskFilters === 'object' &&
      Object.keys(taskFilters).length > 0
    ) {
      return { ...defaultFilters, ...taskFilters };
    }
    return defaultFilters;
  };

  const [filters, setFilters] = useState<TasksListFilter>(getInitialFilters);
  const [sorting, setSorting] = useState<GridSortItem | null>();

  // Context sync is handled by DynamicFilterModal via contextKey on filter groups.
  // DynamicFilterModal writes task filters under filters.taskFilters automatically.

  const [debouncedFilters, setDebouncedFilters] = React.useState<{
    filters: TasksListFilter;
    sorting?: GridSortItem | null;
  }>({ filters: {} as TasksListFilter, sorting: null });

  useDebounce(() => setDebouncedFilters({ filters, sorting }), 500, [
    filters,
    sorting,
  ]);

  const summaries = useGetTaskInfos(
    debouncedFilters.filters,
    debouncedFilters?.sorting,
    !isEmpty(debouncedFilters.filters) && viewState?.view === 'list'
  );

  const { unassignedTasks, fetchUnassignedTasks } = useGetUnassignedTasks(
    defaultFilters,
    filters.status === 'unassigned'
  );
  const unassignedTasksCount = useGetUnassignedTaskCount();

  const onFilterChange = (filters: TasksListFilter) => setFilters(filters);
  const { handleError } = useValidateTaskErrors(notifyError);

  const queryClient = useQueryClient();

  const onComplete = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess('Filter Saved', 'Filter has been successfully saved');
      fetchStoredFilters?.();
      fetchStoredFilterGroups?.();
    } else {
      notifyError('Error', 'Filter was not saved');
    }
  };

  const onRefresh = () => {
    // removeQueries clears out the specific query cache entry. This is needed for schedule data
    // to allow the update of calendars.
    queryClient?.invalidateQueries(['schedule-data']);
    queryClient?.invalidateQueries(['unassigned-task-count']);
    queryClient.invalidateQueries({
      queryKey: ['infos', filters || defaultFilters, sorting],
    });
    fetchUnassignedTasks?.();
  };

  const { upsertTaskFilters } = useUpsertTaskListFilter(
    onComplete,
    userId || ''
  );

  const { orderIndexBulkUpdate } = useFiltersOrderIndexBulkUpdate(
    onComplete,
    userId || ''
  );

  const onTaskFilterGroupReorder = () => {
    fetchStoredFilterGroups?.();
  };

  const { groupsOrderIndexBulkUpdate } = useGroupsOrderIndexBulkUpdate(
    onTaskFilterGroupReorder,
    userId || ''
  );

  const { upsertTaskFilterGroup } = useUpsertTaskListFilterGroup(
    onComplete,
    userId || ''
  );

  const { notifySuccess: successTaskDelete } = useNotificationMutationFactory(
    'Task Filter',
    'delete'
  );
  const onTaskFilterDelete = () => {
    successTaskDelete();
    fetchStoredFilters?.();
  };

  const { deleteTaskFilter } = useDeleteTaskFilter(
    userId || '',
    onTaskFilterDelete
  );

  const { notifySuccess: successTaskFilterGroupDelete } =
    useNotificationMutationFactory('Task Filter Group', 'delete');

  const onTaskFilterGroupDelete = () => {
    successTaskFilterGroupDelete();
    fetchStoredFilterGroups?.();
  };

  const { deleteTaskFilterGroup } = useDeleteTaskFilterGroup(
    userId || '',
    onTaskFilterGroupDelete
  );

  const handleDeleteFilter = (filterId: string) => {
    deleteTaskFilter(filterId);
  };

  const handleDeleteFilterGroup = (groupId: string) => {
    deleteTaskFilterGroup(groupId);
  };

  const handleLossDateFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'> & { lossDate?: string[] }
  ) => {
    let lossDate = currentFilters?.lossDate;

    if (typeof lossDate === 'string') {
      lossDate = JSON.parse(lossDate);
    }
    if (lossDate && lossDate.length > 0) {
      if (lossDate[0]) {
        currentFilters['lossDateFrom'] =
          lossDate[0] && format(new Date(lossDate[0]), 'yyyy-MM-dd');
      }
      if (lossDate[1]) {
        currentFilters['lossDateTo'] =
          lossDate[1] && format(new Date(lossDate[1]), 'yyyy-MM-dd');
      }
    }
  };

  const handleNoticeDateFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'> & {
      noticeDate?: string[];
    }
  ) => {
    let noticeDate = currentFilters?.noticeDate;
    if (typeof noticeDate === 'string') {
      noticeDate = JSON.parse(noticeDate);
    }
    if (noticeDate && noticeDate.length > 0) {
      if (noticeDate[0]) {
        currentFilters['noticeDateFrom'] =
          noticeDate[0] && format(new Date(noticeDate[0]), 'yyyy-MM-dd');
      }
      if (noticeDate[1]) {
        currentFilters['noticeDateTo'] =
          noticeDate[1] && format(new Date(noticeDate[1]), 'yyyy-MM-dd');
      }
    }
  };

  const handleAppointmentDateFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'> & {
      appointmentDate?: string[];
    }
  ) => {
    let appointmentDate = currentFilters?.appointmentDate;
    if (typeof appointmentDate === 'string') {
      appointmentDate = JSON.parse(appointmentDate);
    }
    if (appointmentDate && appointmentDate.length > 0) {
      if (appointmentDate[0]) {
        currentFilters['appointmentDateFrom'] =
          appointmentDate[0] &&
          format(new Date(appointmentDate[0]), 'yyyy-MM-dd');
      }
      if (appointmentDate[1]) {
        currentFilters['appointmentDateTo'] =
          appointmentDate[1] &&
          format(new Date(appointmentDate[1]), 'yyyy-MM-dd');
      }
    }
  };

  const handleReceivedDateFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'> & {
      receivedDate?: string[];
    }
  ) => {
    let receivedDate = currentFilters?.receivedDate;

    if (typeof receivedDate === 'string') {
      receivedDate = JSON.parse(receivedDate);
    }
    if (receivedDate && receivedDate.length > 0) {
      if (receivedDate[0]) {
        currentFilters['receivedDateFrom'] =
          receivedDate[0] && format(new Date(receivedDate[0]), 'yyyy-MM-dd');
      }
      if (receivedDate[1]) {
        currentFilters['receivedDateTo'] =
          receivedDate[1] && format(new Date(receivedDate[1]), 'yyyy-MM-dd');
      }
    }
  };

  const handleResourceAttributeFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'>
  ) => {
    let resourceAttributes = currentFilters?.resourceAttributes;

    if (typeof resourceAttributes === 'string') {
      resourceAttributes = JSON.parse(
        resourceAttributes
      ) as ResourceAttribute[];
    }

    const attributeFiltersWithValue =
      companyAttributes?.companyAttributes?.filter(
        (attribute) => currentFilters?.[attribute.value]
      );

    const attributes = resourceAttributes as ResourceAttribute[];

    attributeFiltersWithValue?.forEach((attribute) => {
      attributes?.push({
        attributeDefinition: attribute.title,
        values: JSON.parse(currentFilters?.[attribute.title] as string),
      });
    });

    if (attributes && attributes.length > 0) {
      currentFilters.resourceAttributes = JSON.stringify(attributes);
    }
  };

  const handleSaveFilters = (values: unknown) => {
    const filtersToSave = values as TasksListFilter & {
      lossDate: string[];
      noticeDate: string[];
      appointmentDate: string[];
      receivedDate: string[];
    };
    handleLossDateFilters(filtersToSave);
    handleNoticeDateFilters(filtersToSave);
    handleAppointmentDateFilters(filtersToSave);
    handleReceivedDateFilters(filtersToSave);
    handleResourceAttributeFilters(filtersToSave);

    upsertTaskFilters(filtersToSave);
  };

  const filterOrderIndexBulkUpdate = (
    values: (Omit<TasksListFilter, 'pageSize'> & { isNew?: boolean })[]
  ) => {
    values.map((value) => {
      if (value.isNew) {
        handleLossDateFilters(value);
        handleNoticeDateFilters(value);
        handleAppointmentDateFilters(value);
        handleReceivedDateFilters(value);
        handleResourceAttributeFilters(value);
        delete value.isNew;
      }
      return value;
    });
    orderIndexBulkUpdate(values);
  };

  const handleSaveFilterGroup = (values: TaskFilterGroup) => {
    upsertTaskFilterGroup(values);
  };

  const groupsOrderIndexBulkUpdateHandler = (values: TaskFilterGroup[]) => {
    groupsOrderIndexBulkUpdate(values);
  };

  const sortedUnassignedTasks = React.useMemo(() => {
    const excludedQueues = ['cancelled_q', 'wip_q', 'egress'];
    const unassigned: TaskInfo[] | undefined = unassignedTasks?.filter(
      (task: TaskInfo) => !excludedQueues.includes(task?.targetQueue)
    );

    return _.orderBy(
      unassigned,
      [(task: TaskInfo) => new Date(task?.appointment)],
      ['asc']
    );
  }, [unassignedTasks]);

  const handleSort = ([value]: GridSortModel) => {
    setSorting(value);
    summaries.goToFirstPage();
  };

  React.useEffect(() => {
    // update filter for timeline
    const currentFilters = { ...filters };
    currentFilters.scheduleTimeZoneId = cachedTimeZoneValue;

    setFilters({ ...currentFilters, pageSize: filters?.pageSize || 10 });
  }, [cachedTimeZone]);

  React.useEffect(() => {
    if (!viewState.showMap) {
      setMultiSelected([]);
    }
  }, [viewState.showMap]);

  const multiSelectedResources = React.useMemo(() => {
    return selectedResources?.flatMap((selected) => {
      return selected.data || [];
    });
  }, [selectedResources]);

  const loadingResources = React.useMemo(() => {
    return (
      selectedResources.filter((resource) => resource.status === 'loading')
        .length > 0
    );
  }, [selectedResources]);

  const groupOptions = React.useMemo(() => {
    return groups?.map((group) => ({
      title: group.name,
      value: group.integrationGUID,
    })) as MultiSelectOption[];
  }, [groups]);

  const filterOptionsLookup = React.useMemo(() => {
    return {
      teamOptions: teamOptions || [],
      regionOptions: regionOptions || [],
      perilOptions: perilOptions || [],
      methodOfInspectionOptions: methodOfInspectionOptions || [],
      statesOptions,
      officeOptions,
      companyRoles,
      groupOptions,
    };
  }, [
    groupOptions,
    teamOptions,
    regionOptions,
    perilOptions,
    methodOfInspectionOptions,
    statesOptions,
    officeOptions,
    companyRoles,
  ]);

  const [showFilterState, setShowFilterState] = React.useState<
    Record<string, boolean>
  >({ task_filters: true, resource_filters: true });

  return (
    <MapProvider>
      <>
        <TasksList
          data={summaries}
          perils={perils || []}
          multiSelected={multiSelectedResources}
          onFilterChange={onFilterChange}
          handleSort={handleSort}
          filters={filters || defaultFilters}
          onRefresh={onRefresh}
          storedFilters={storedFiltersWithGroups || []}
          viewState={viewState}
          setViewState={setViewState}
          selectedResourcesLoading={loadingResources}
          permissions={permissions}
          isWorkingTimeFilteredState={{
            isNonWorkingTimeFiltered,
            setIsNonWorkingTimeFiltered,
          }}
          handleSaveFilters={handleSaveFilters}
          handleOrderIndexBulkUpdate={filterOrderIndexBulkUpdate}
          handleGroupsOrderIndexBulkUpdate={groupsOrderIndexBulkUpdateHandler}
          handleSaveFilterGroup={handleSaveFilterGroup}
          handleDeleteFilter={handleDeleteFilter}
          handleDeleteFilterGroup={handleDeleteFilterGroup}
          filterOptions={filterOptionsLookup}
          setResourceFilterInput={setResourceFilterInput}
          setLossCountyFilterInput={setLossCountyFilterInput}
          setLossStateFilterInput={setLossStateFilterInput}
          setRsrcCountyFilterInput={setRsrcCountyFilterInput}
          setRsrcStateFilterInput={setRsrcStateFilterInput}
          filteredResources={filteredResources}
          mappedFilteredResources={mappedFilteredResources || []}
          filteredLossCounties={filteredLossCounties?.data || []}
          filteredLossCountiesStatus={filteredLossCounties?.status}
          filteredRsrcCounties={filteredRsrcCounties?.data || []}
          filteredRsrcCountiesStatus={filteredRsrcCounties?.status}
          companyAttributes={companyAttributes.companyAttributes}
          attributeOptions={attributeOptions}
          unassignedCount={unassignedTasksCount.data?.count || 0}
          showFilterState={{ showFilterState, setShowFilterState }}
        />
        {/*
              Because we use outlets the error boundary has to go here, or else the error
              boundary modal will not show
          */}
        <ErrorBoundary
          boundaryName="TaskDetailsOutlet"
          onError={(error, errorInfo) => {
            trackError(error, {
              boundaryName: 'TaskDetailsOutlet',
              componentStack: errorInfo.componentStack,
              url: window.location.href,
              timestamp: new Date().toISOString(),
            });
          }}
          fallback={(error, errorInfo, reset) => (
            <BladeErrorFallback
              error={error}
              errorInfo={errorInfo}
              reset={reset}
              title="Unable to Load Task Details"
              description="This task encountered an error and could not be loaded. The task may have invalid data."
              closeUrl="/tasks"
            />
          )}
        >
          <Outlet />
        </ErrorBoundary>
      </>
    </MapProvider>
  );
};
